'use client';

import { Box, CircularProgress } from '@mui/material';
import { useRouter } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';

import { DynamicFavicon, SEOHead } from '@/components/atoms';
import Footer from '@/components/organisms/Footer';
import Header from '@/components/organisms/Header';
import { useFooterData, useHeaderData, useLandingPageData } from '@/hooks/api';

/**
 * General Layout Component
 *
 * Provides a consistent layout structure for all pages with:
 * - Dynamic favicon
 * - SEO head
 * - Header with navigation
 * - Main content area
 * - Footer with company information
 *
 * @param children - The main content to render
 * @param seoData - Optional SEO data to override defaults
 * @param showLoading - Whether to show loading state
 */
export interface GeneralLayoutProps {
  children: ReactNode;
  seoData?: {
    title?: string;
    description?: string;
    keywords?: string;
    image?: any;
  };
  showLoading?: boolean;
}

const GeneralLayout = ({
  children,
  seoData,
  showLoading = false,
}: GeneralLayoutProps) => {
  const router = useRouter();
  // State to track if component has mounted (prevents hydration mismatch)
  const [isMounted, setIsMounted] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Fetch global data for header, footer, and site settings
  const {
    siteData,
    seoData: globalSeoData,
    loading: landingPageLoading,
    error: landingPageError,
  } = useLandingPageData();

  // Fetch header and footer data separately
  const {
    logo: headerLogo,
    navigationItems: headerNavigationItems,
    isActive: headerIsActive,
    loading: headerLoading,
    error: headerError,
  } = useHeaderData();

  const {
    companyName: footerCompanyName,
    companyDescription: footerCompanyDescription,
    contactInfo: footerContactInfo,
    socialLinks: footerSocialLinks,
    footerSections: footerFooterSections,
    copyright: footerCopyright,
    loading: footerLoading,
    error: footerError,
  } = useFooterData();

  // Combine loading states
  const loading = landingPageLoading || headerLoading || footerLoading;
  const error = landingPageError || headerError || footerError;

  // Set mounted state after component mounts
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Error boundary effect
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Only handle React DOM errors related to removeChild
      if (
        event.error &&
        event.error.message &&
        event.error.message.includes('removeChild')
      ) {
        console.warn('Caught removeChild error, attempting to recover...');
        setHasError(false); // Reset error state
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Merge SEO data (page-specific overrides global)
  const finalSeoData = {
    ...globalSeoData,
    ...seoData,
  };

  // Always render the same structure to prevent hydration mismatch
  // Use opacity to hide/show content instead of conditional rendering
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'white' }}>
      {/* Dynamic Favicon - Only render when mounted and no errors */}
      {isMounted && !hasError && (
        <DynamicFavicon faviconUrl={siteData.favicon} />
      )}

      {/* SEO Head */}
      <SEOHead
        title={finalSeoData.metaTitle}
        description={finalSeoData.metaDescription}
        keywords={finalSeoData.keywords}
        image={finalSeoData.metaImage}
        siteName={siteData.siteName}
      />

      {/* Loading State - Overlay approach to prevent hydration mismatch */}
      {isMounted && (showLoading || loading) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Error State - Overlay approach to prevent hydration mismatch */}
      {isMounted && error && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            p: 4,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <h2>Error Loading Page</h2>
            <p>{error}</p>
            <button
              onClick={() => router.refresh()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#EB722E',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
              }}
            >
              Reload Page
            </button>
          </Box>
        </Box>
      )}

      {/* Header - Always render but conditionally show content */}
      <Box
        sx={{
          opacity: isMounted ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      >
        <Header
          logo={headerLogo}
          navigationItems={headerNavigationItems}
          isActive={headerIsActive}
        />
      </Box>

      {/* Main Content - Always render with consistent margin */}
      <Box
        sx={{
          mt: { xs: '70px', md: '90px' }, // Always apply header margin to prevent hydration mismatch
          opacity: isMounted ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      >
        {children}
      </Box>

      {/* Footer - Always render but conditionally show content */}
      <Box
        sx={{
          opacity: isMounted ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      >
        <Footer
          companyName={footerCompanyName}
          companyDescription={footerCompanyDescription}
          contactInfo={footerContactInfo}
          socialLinks={footerSocialLinks}
          footerSections={footerFooterSections}
          copyright={footerCopyright}
        />
      </Box>
    </Box>
  );
};

export default GeneralLayout;
